import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { useBrand } from '@/brand/context'
import { DiscoveryRulesForm } from '@/settings/discovery-rules/form'
import type { DiscoveryRulesValue } from '@/settings/discovery-rules/form'
import { ErrorState, Loading } from '@/ui/states'
import { SettingsStepShell } from './SettingsStepShell'
import { useSettleStep } from './settingsStep'

/** Step 7 — what counts as a lead, through the same form `/settings/discovery-rules` uses. */
export function DiscoveryRulesStep(): JSX.Element {
  const brand = useBrand()
  const discovery = useQuery(
    (signal) => call(() => api.GET('/api/v1/outreach/keywords', { signal })),
    [],
  )
  const save = useMutation((body: DiscoveryRulesValue) =>
    call(() => api.PUT('/api/v1/outreach/discovery-settings', { body })),
  )
  const { settle, failure } = useSettleStep('discovery_rules')

  return (
    <SettingsStepShell step="discovery_rules" failure={failure}>
      {discovery.state.status === 'loading' ? <Loading label="Loading" lines={2} /> : null}
      {discovery.state.status === 'error' ? (
        <ErrorState failure={discovery.state.failure} onRetry={discovery.refetch} />
      ) : null}
      {discovery.state.status === 'ready' ? (
        <DiscoveryRulesForm
          value={{
            max_age_hours: discovery.state.data.settings.max_age_hours,
            exclude_phrases: discovery.state.data.settings.exclude_phrases,
          }}
          customised={discovery.state.data.settings.customised}
          productName={brand.productName}
          submitLabel="Continue"
          onSubmit={async (next) => {
            const result = await save.run(next)
            if (result.ok) await settle()
            return result
          }}
        />
      ) : null}
    </SettingsStepShell>
  )
}
